import { isPromise } from '@kuow/core'
import type { DonationEvent } from '@kuow/core'

export const TIMELINE_EVENT_TYPES: ReadonlySet<string> = new Set([
  'payment_completed',
  'payment_refunded',
  'gift_changed',
])

export const isPaymentEvent = (e: DonationEvent): boolean =>
  e.event_type === 'payment_completed' || e.event_type === 'payment_refunded'

export const isGiftEvent = (e: DonationEvent): boolean => e.event_type === 'gift_changed'

const capitalize = (value: string): string =>
  value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : ''

export function formatActivityDate(occurredAt: string): string {
  if (!occurredAt) return ''
  // occurred_at carries its own UTC offset (e.g. 2026-06-16T19:16:47.000-07:00). Read the
  // calendar date straight from the ISO prefix so the displayed date matches the timestamp's
  // own zone and never shifts with the viewer's timezone (a Date round-trip would render the
  // viewer-local day, which differs near midnight for non-Pacific viewers).
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(occurredAt)
  if (iso) {
    const [, yyyy, mm, dd] = iso
    return `${mm}.${dd}.${yyyy.slice(-2)}`
  }
  // Fallback for non-ISO inputs.
  const date = new Date(occurredAt)
  if (Number.isNaN(date.getTime())) return occurredAt
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  const yy = String(date.getFullYear()).slice(-2)
  return `${mm}.${dd}.${yy}`
}

export function eventTitle(e: DonationEvent): string {
  if (isGiftEvent(e)) return 'Gift Selected'
  return e.monthly ? 'Monthly Donation' : 'One-Time Donation'
}

export function eventMethodLabel(e: DonationEvent): string | null {
  if (e.account_type === 'credit_card') {
    const brand = capitalize(e.card_brand ?? '')
    const last4 = e.card_last4 ?? ''
    if (brand && last4) return `${brand} *${last4}`
    if (last4) return `Card *${last4}`
    if (brand) return brand
    return 'Credit Card'
  }
  if (e.account_type === 'bank_account') return e.card_last4 ? `Bank *${e.card_last4}` : 'Bank Account'
  if (e.account_type === 'paypal') return 'PayPal'
  if (e.account_type === 'bill_later') return isPromise(e.account_type, e.delivery_method) ? 'Promise' : 'Bill Me Later'
  if (e.account_type === 'apple_pay') return 'Apple Pay'
  return null
}

export function statusBadgeFor(
  status: string | null,
): { label: string; bg: string; fg: string } | null {
  switch (status) {
    case 'completed':
      return { label: 'Completed', bg: 'bg.success', fg: 'fg.success' }
    case 'refunded':
      return { label: 'Refunded', bg: 'bg.muted', fg: 'fg.muted' }
    default:
      return null
  }
}
